import cn from "classnames"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { Input } from "reactstrap"
import { AccordionWithChildren, getKeys, InputLabel, useSelector } from "swiipe.portal.shared"
import { calculateTranslationValue } from "../../../services/translationService"
import { ITranslationChange, translationActions, translationSelectors } from "../../../store/reducers/translationSlice"
import { StoreState } from "../../../store/StoreState"
import { syncTranslationChangesThunk } from "../../../store/thunks/translationsThunks"
import useReduxDispatch from "../../../store/useReduxDispatch"
import { ITranslationItem } from "../../../type/translations/ITranslationItem"
import "./TranslationItem.scss"
import TranslationItemChangesBox from "./TranslationItemChangesBox"
import TranslationSelection from "./TranslationSelection"
import TranslationTagField from "./TranslationTagField"

interface ITranslationItemProps {
    item: ITranslationItem
}

const TranslationItem = ({ item }: ITranslationItemProps) => {
    const dispatch = useReduxDispatch()
    const changesForItemState = useSelector((state: StoreState) => translationSelectors.changesByItemId(state, item.id))
    const changesForItem = changesForItemState ?? []
    const changesForItemRef = useRef<ITranslationChange[] | undefined>(changesForItem)
    changesForItemRef.current = changesForItem
    const currentLanguage = useSelector(translationSelectors.currentLanguage)
    const languageConfigs = useSelector(translationSelectors.languageConfigs)
    const textChangeTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
    const languageConfigsSorted = useMemo(
        () =>
            languageConfigs && [
                languageConfigs.find((lc) => lc.language === currentLanguage)!,
                ...languageConfigs.filter((lc) => lc.language !== currentLanguage),
            ],
        [languageConfigs, currentLanguage]
    )

    const [texts, setTexts] = useState<{ [lang: string]: string | undefined }>({})

    useEffect(() => {
        setTexts(
            (languageConfigs ?? []).reduce((res, lc) => {
                res[lc.language] = calculateTranslationValue(item, changesForItem, lc.language)
                return res
            }, {} as { [lang: string]: string | undefined })
        )
    }, [changesForItemState])

    useEffect(() => {
        if (textChangeTimeoutRef.current) {
            clearTimeout(textChangeTimeoutRef.current)
        }
        textChangeTimeoutRef.current = setTimeout(() => {
            getKeys(texts).forEach((lang) => {
                if (calculateTranslationValue(item, changesForItemRef.current ?? [], lang) !== texts[lang]) {
                    dispatch(
                        translationActions.addChanges({
                            changes: [
                                {
                                    group: item.group,
                                    language: lang,
                                    key: item.key,
                                    value: texts[lang] ?? "",
                                    itemId: item.id,
                                    type: "UserChange",
                                    created: new Date().toISOString(),
                                },
                            ],
                        })
                    )
                    dispatch(syncTranslationChangesThunk())
                }
            })
        }, 200)
    }, [texts])

    const showMissingTranslationCounters = useSelector(translationSelectors.showMissingTranslationCounters)
    const missingLanguages = showMissingTranslationCounters
        ? languageConfigs?.filter((lc) => !calculateTranslationValue(item, changesForItem, lc.language))?.map((lc) => lc.language)
        : undefined
    const textExample = item.base || item.translations.find((t) => !!t.content)?.content || item.key || ""
    const change = changesForItem.find((c) => c.language === currentLanguage)
    const otherChanges = changesForItem.filter((c) => c.language !== currentLanguage)
    return (
        <div key={item.group + "_" + item.key + "_" + currentLanguage} className={cn("translation-item")}>
            <div className="translation-item-key">
                {item.key}
                <TranslationTagField group={item.group} tagKey={item.key} className="translation-item__tags" />
            </div>
            <AccordionWithChildren
                className="translation-item__languages-container"
                header={(isExpanded) => (
                    <div className="translation-item__languages-container-header">
                        <TranslationSelection
                            className="translation-section__selection"
                            group={item.group}
                            levels={item.levels}
                        />
                        <div
                            className={cn(
                                "translation-item-value__container",
                                isExpanded && "translation-item-value__container--hidden"
                            )}
                        >
                            <Input
                                className={cn(
                                    "translation-item-value",
                                    change && "translation-item-value--changes",
                                    missingLanguages?.includes(currentLanguage) && "translation-item-value--missing"
                                )}
                                onChange={(e) => {
                                    setTexts({ ...texts, [currentLanguage]: e.currentTarget.value })
                                }}
                                placeholder=""
                                value={texts[currentLanguage]}
                                type="textarea"
                                rows={Math.ceil(textExample.length / 100)}
                            ></Input>
                            <TranslationItemChangesBox
                                item={item}
                                changeForItemAndLanguage={change}
                                language={currentLanguage}
                                className="translation-item-value__changes-box-primary"
                            />
                            {otherChanges.length > 0 && <div className="translation-item-value__other-changes"></div>}
                            {missingLanguages && missingLanguages.filter((ml) => ml !== currentLanguage).length > 0 && (
                                <div className="translation-item-value__other-missing"></div>
                            )}
                        </div>
                    </div>
                )}
                animate={false}
                leftAlignHeader
                arrowLeftOfHeader
                onlyRenderVisibleChildren
                ignoreHeaderClick
            >
                <div className="translation-item__languages-container-inner">
                    {languageConfigsSorted?.map((lc) => {
                        const langChange = changesForItem.find((c) => c.language === lc.language)
                        return (
                            <div key={lc.language}>
                                <InputLabel title={lc.label}>
                                    <div className="translation-item__languages-container-inner-wrapper">
                                        <TranslationSelection
                                            className="translation-section__selection"
                                            group={item.group}
                                            levels={item.levels}
                                            language={lc.language}
                                        />
                                        <Input
                                            className={cn(
                                                "translation-item-value",
                                                langChange && "translation-item-value--changes",
                                                missingLanguages?.includes(lc.language) && "translation-item-value--missing"
                                            )}
                                            onChange={(e) => {
                                                setTexts({ ...texts, [lc.language]: e.currentTarget.value })
                                            }}
                                            placeholder=""
                                            value={texts[lc.language]}
                                            type="textarea"
                                            rows={Math.ceil(textExample.length / 100)}
                                        ></Input>
                                        <TranslationItemChangesBox
                                            item={item}
                                            changeForItemAndLanguage={langChange}
                                            language={lc.language}
                                        />
                                    </div>
                                </InputLabel>
                            </div>
                        )
                    })}
                </div>
            </AccordionWithChildren>
        </div>
    )
}

export default TranslationItem
